import { jwtDecode } from 'jwt-decode'

/** The claims this application reads. Others are present and ignored. */
export interface TokenPayload {
  exp: number
  iat: number
  user_id?: number
}

export function decodeToken(token: string): TokenPayload | null {
  try {
    const payload = jwtDecode<Partial<TokenPayload>>(token)
    if (typeof payload.exp !== 'number' || typeof payload.iat !== 'number') {
      return null
    }
    return payload as TokenPayload
  } catch {
    // A tampered, truncated or simply non-JWT cookie value. Treated the same as
    // having no token, because the alternative is throwing out of whatever read it.
    return null
  }
}

/**
 * Whether a stored refresh token is worth trying.
 *
 * A token in its last tenth of life is close enough to expiry that spending a
 * round trip on it, and a render of a signed-in shell that is about to be torn
 * down, is worse than going straight to the sign-in page.
 */
export function hasEnoughLifeLeft(
  token: string,
  fraction = 0.1,
  now = Date.now(),
): boolean {
  const payload = decodeToken(token)
  if (payload === null) return false

  const lifespan = payload.exp - payload.iat
  if (lifespan <= 0) return false

  return (payload.exp - now / 1000) / lifespan > fraction
}

/**
 * Whether the access token is far enough through its life to renew.
 *
 * Measured from when this client received the token rather than from its `iat`,
 * because the two differ by however much clock skew there is between the browser
 * and the backend, and a browser running fast would otherwise refresh on every
 * single request.
 */
export function isPastRefreshThreshold(
  payload: TokenPayload,
  receivedAt: number,
  threshold = 0.8,
  now = Date.now(),
): boolean {
  const lifespan = (payload.exp - payload.iat) * 1000
  if (lifespan <= 0) return true

  return (now - receivedAt) / lifespan > threshold
}

/** Milliseconds until the access token reaches the refresh threshold. */
export function millisecondsUntilRefresh(
  payload: TokenPayload,
  receivedAt: number,
  threshold = 0.8,
  now = Date.now(),
): number {
  const lifespan = (payload.exp - payload.iat) * 1000
  return Math.max(0, receivedAt + lifespan * threshold - now)
}
